import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { CASE_STUDIES } from "@/lib/insights";
import { useLang } from "@/lib/LangContext";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const c = CASE_STUDIES.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/case-studies" replace />;

  const related = CASE_STUDIES.filter((x) => x.slug !== slug && x.tag === c.tag).slice(0, 3);

  return (
    <div data-testid={`page-case-${c.slug}`}>
      <Seo
        title={`${c.threat} — ${c.cityName}, NB | Case Study`}
        description={c.chapters.challenge.slice(0, 155)}
        path={`/case-studies/${c.slug}`}
        type="article"
      />
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={c.hero} alt="" className="w-full h-full object-cover grayscale contrast-125" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Link
            to="/case-studies"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-primary transition-colors mb-8 inline-block"
          >
            ← {lang === "fr" ? "Tous les cas" : "All Case Studies"}
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6 flex flex-wrap gap-3 items-center">
            <MapPin size={12} strokeWidth={1.5} />
            <span>{c.region}</span><span>·</span><span>{c.coords}</span><span>·</span><span>{c.date}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-4xl md:text-7xl leading-[1] tracking-tight max-w-4xl"
          >
            {c.threat} <br />
            <i className="text-primary">— {c.cityName}.</i>
          </motion.h1>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-8 space-y-10">
            <Chapter label={lang === "fr" ? "Le défi" : "The Challenge"} body={c.chapters.challenge} />
            <Chapter label={lang === "fr" ? "Notre approche" : "Our Approach"} body={c.chapters.approach} />
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                [ {lang === "fr" ? "Résultat" : "Outcome"} ]
              </div>
              <ul className="space-y-3">
                {c.chapters.outcome.map((o, k) => (
                  <li key={k} className="flex items-start gap-4 border-b border-border pb-4">
                    <span className="font-mono text-[10px] tracking-widest text-primary pt-2">
                      {String(k + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-lg md:text-xl leading-snug">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-32 border border-border p-6 bg-[hsl(0_0%_6%)] space-y-4 font-mono text-xs tracking-widest">
              <Row k={lang === "fr" ? "Communauté" : "Community"} v={c.cityName} to={`/cities/${c.citySlug}`} />
              <Row k={lang === "fr" ? "Région" : "Region"} v={c.region} />
              <Row k={lang === "fr" ? "Menace" : "Threat"} v={c.tag} />
              <Row k={lang === "fr" ? "Statut" : "Status"} v={lang === "fr" ? "Résolu" : "Resolved"} accent />
              <Link
                to="/contact#quote"
                className="mt-6 block text-center bg-primary text-primary-foreground px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
              >
                {lang === "fr" ? "Demander un devis" : "Request Quote"}
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              [ {lang === "fr" ? "Cas similaires" : "Similar Cases"} ]
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-l border-border">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/case-studies/${r.slug}`}
                  data-testid={`case-related-${r.slug}`}
                  className="border-r border-b border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
                >
                  <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-3">
                    {r.region} · {r.date}
                  </div>
                  <h4 className="font-serif text-2xl leading-tight group-hover:text-primary transition-colors">
                    {r.cityName}
                  </h4>
                  <p className="font-serif italic text-base text-foreground/70 mt-2">{r.threat}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <QuoteForm defaultCity={c.cityName} />
    </div>
  );
}

function Chapter({ label, body }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
        [ {label} ]
      </div>
      <p className="text-base md:text-lg leading-relaxed text-foreground/85 max-w-2xl">{body}</p>
    </div>
  );
}

function Row({ k, v, accent, to }) {
  return (
    <div className="flex justify-between items-start border-b border-border pb-3">
      <span className="text-muted-foreground uppercase">{k}</span>
      {to ? (
        <Link to={to} className="hover:text-primary transition-colors text-right">{v}</Link>
      ) : (
        <span className={`text-right ${accent ? "text-primary" : ""}`}>{v}</span>
      )}
    </div>
  );
}
